'use client';

import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithRef, ReactNode, useMemo, useRef, useState } from 'react';
import { AddressCard } from '@/features/address/ui/address-card';
import { AddressIconOption } from '@/features/address/ui/address-icon/types';
import { ConfirmDeleteAddressModal } from '@/features/address/ui/confirm-delete-address-modal';
import { QueryApi } from '@/features/query/api';
// import { AddEditAddressModal } from '@/features/address/ui/add-edit-address-modal';
import { Address, UserAddressType } from '@/shared/model/Address';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import { Button } from '@/shared/ui/button';
import { mergeRefs } from '@/shared/ui/form/utils/mergeRefs';
import { Icon } from '@/shared/ui/icon';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { Select } from '@/shared/ui/select';
import { TimeBadge } from '@/shared/ui/time-badge';
import './address-select.scss';

export type AddressItem = {
    id: string;
    title: string;
    address: Address;
    icon: string;
    isDefault: boolean;
    isEditable?: boolean;
};

type Props = Omit<ComponentPropsWithRef<typeof Select>, 'children' | 'value' | 'onValueChange'> & {
    time?: number;
    triggerProps?: WithDataAttributes<ComponentPropsWithRef<typeof Select.Trigger>>;
    addressTestId?: string;
    addresses?: AddressItem[];
    isLoading?: boolean;
    hideAddButton?: boolean;
    onSuccess?: (id: string | null, address: Address | null) => void;
    value?: string | Pick<Address, 'id'>;
    onValueChange?: (id: string | null, address: Address | null) => void;
    badge?: ReactNode;
    additionalButtons?: ReactNode;
    defaultOpen?: boolean;
};
export const AddressSelect = ({
    time,
    value,
    onValueChange,
    defaultValue,
    triggerProps,
    addressTestId,
    addresses = [],
    isLoading,
    hideAddButton,
    onSuccess,
    badge,
    additionalButtons,
    defaultOpen,
    ...props
}: Props) => {
    const ref = useRef<HTMLButtonElement>(null);
    const t = useTranslations();
    const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
    const deleteAddressMutation = useMutation(QueryApi.Customer.mutations.DeleteAddress);

    const id = useMemo(() => {
        const val = value ?? defaultValue;
        if (typeof val === 'object') {
            return val?.id;
        }
        return val;
    }, [value, defaultValue]);
    const selectedAddress = useMemo(() => addresses.find((address) => address.id === id), [id, addresses]);

    const handleEdit = (addressId: string) => {
        setIsOpen(false);
        const addressItem = addresses.find((a) => a.id === addressId);
        ModalService.showPrefab('AddEditAddressModal', {
            id: addressId,
            defaultValues: addressItem
                ? {
                      address: {
                          street: addressItem.address.street,
                          city: addressItem.address.city,
                          postCode: addressItem.address.postCode,
                          country: addressItem.address.country,
                          lat: addressItem.address.lat ?? undefined,
                          lon: addressItem.address.lon ?? undefined,
                      },
                      name: addressItem.title,
                      icon: addressItem.icon as AddressIconOption,
                      addressType: UserAddressType.OTHER,
                  }
                : undefined,
            onSuccess,
        });
    };
    const handleAdd = () => {
        setIsOpen(false);
        ModalService.showPrefab('AddEditAddressModal', {
            onSuccess,
        });
    };

    const handleDeleteClick = (addressId: string) => {
        const addressItem = addresses.find((a) => a.id === addressId);
        if (addressItem) {
            setIsOpen(false);
            ModalService.show(ConfirmDeleteAddressModal, {
                addressName: addressItem.title,
                onConfirm: async () => {
                    const newAddress = addresses.find((a) => a.id !== addressId) ?? null;
                    await deleteAddressMutation.mutateAsync({ addressId: addressItem.id });
                    if (addressItem.id === id) {
                        onSuccess?.(
                            newAddress?.id ?? null,
                            newAddress ? { id: newAddress.id, ...newAddress?.address } : null,
                        );
                    }
                },
            });
        }
    };

    return (
        <Select
            {...props}
            value={id}
            onValueChange={(id) => {
                const address = addresses.find((address) => address.id === id)?.address;
                if (!address) {
                    return;
                }
                onValueChange?.(id, address);
            }}
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <Select.Trigger
                className="address-select__trigger"
                variant="secondary"
                {...triggerProps}
                ref={mergeRefs([ref, triggerProps?.ref])}
            >
                {selectedAddress ? (
                    <AddressCard
                        address={selectedAddress.address}
                        name={selectedAddress.title}
                        icon={selectedAddress.icon as AddressIconOption}
                        right={badge ?? (time ? <TimeBadge time={time} /> : undefined)}
                        className="address-select__address-card"
                    />
                ) : (
                    <span>{isLoading ? t('WORDS.LOADING') : t('ADDRESS_SELECT.PLACEHOLDER')}</span>
                )}
            </Select.Trigger>
            <Select.Content variant="secondary" style={{ zIndex: 1000 }}>
                {addresses.map((item) => (
                    <Select.Item
                        value={item.id}
                        key={item.id}
                        variant="rounded"
                        right={
                            item.isEditable !== false && (
                                <div className="address-select__actions">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(item.id);
                                        }}
                                        className="address-select__edit-button"
                                        data-testid="address-select-edit-button"
                                    >
                                        <Icon name="edit" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(item.id);
                                        }}
                                        className="address-select__delete-button"
                                        data-testid="address-select-delete-button"
                                    >
                                        <Icon name="trash" />
                                    </button>
                                </div>
                            )
                        }
                        data-testid={addressTestId}
                    >
                        <AddressCard
                            address={item.address}
                            name={item.title}
                            icon={item.icon as AddressIconOption}
                        />
                    </Select.Item>
                ))}
                <div className="address-select__buttons">
                    {!hideAddButton && (
                        <Button
                            variant="outlined"
                            className="address-select__button"
                            onClick={handleAdd}
                            data-testid="address-select-add-button"
                        >
                            <Icon name="plus" width={16} height={16} />
                            {t('ADDRESS_SELECT.ADD_NEW_ADDRESS')}
                        </Button>
                    )}
                    {additionalButtons}
                </div>
            </Select.Content>
        </Select>
    );
};
